import { call, all, select, put, type CallEffect } from 'redux-saga/effects';
import { RecipientRole } from '../../../common/recipients/types';
import * as templateApi from '../../../lib/api/template/template-api';
import { getPdfBlobFromUrl, type ITemplateResponse } from '../../../lib/api/template/template-api';

import { BLOCK_TYPE_KEYS, STRIKE_LINE_WIDTH } from '../../../lib/constants';
import pdfCache from '../../../lib/pdfCache';
import { calculateFontSize } from '../../../lib/useAutomaticFontSizing';
import { cleanUpInvalidBlockValuesAndLogToFaro, getRecipientColor, sortRecipients } from '../../../lib/utils';
import type { IRootState } from '../../index';
import { errorToast } from '../../pageFrame/actions';
import type { ITemplate, ITemplateRecipient } from '../../templateCreate/template-types';
import * as actions from '../actions';
import { DocumentStatus, type IBlock, type IGroup, type IPageDimensions, type ProcessedEnvelope } from '../types';

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const selectZoom = (state: IRootState) => state.senderBuild.zoom;
// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const getId = (state: IRootState) => state.senderBuild.id;

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const prepareBlockForTemplateService = (block: IBlock) => {
  const templateApiBlock: any = { ...block };
  if (templateApiBlock.readOnly) {
    if (templateApiBlock.blockType === BLOCK_TYPE_KEYS.TEXT) {
      templateApiBlock.blockType = 'ReadOnlyText';
    } else if (templateApiBlock.blockType === BLOCK_TYPE_KEYS.CHECKBOX) {
      templateApiBlock.blockType = 'ReadOnlyCheckbox';
    }
  }
  if (templateApiBlock.groupId) {
    templateApiBlock.blockGroupId = block.groupId;
    delete templateApiBlock.groupId;
  }

  delete templateApiBlock.isEditingDisabled;
  return templateApiBlock;
};

export function* templateFetch(id: string) {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const zoom = yield select(selectZoom);
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const res = yield call(templateApi.getTemplate, id);
  const template = res.result!;
  if (res.status !== 200) {
    throw Error(`Non-200 Status Code of Template GET: ${res.status}`);
  }

  yield call(cacheTemplatePdfBlobs, template);
  return processTemplate(template, zoom);
}

/** Cache PDF blobs for each document in the template */
export async function cacheTemplatePdfBlobs(template: ITemplate) {
  // Download and cache PDF blobs for all documents (similar to templateFetch saga)
  const docBlobs: ArrayBuffer[] = await Promise.all(
    template.documents.map((doc: any) => getPdfBlobFromUrl(doc.links!.downloadPDF!.href!))
  );

  // Cache PDF blobs
  template.documents.forEach((doc: any, i: number) => {
    pdfCache[doc.id!] = docBlobs[i];
  });
}

/**
 * @returns {ProcessedEnvelope} Forcing template shape into envelope shape
 */
export function processTemplate(template: ITemplate, zoom: number): ProcessedEnvelope {
  const blocks = {};
  template.documents.forEach((doc) => {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    blocks[doc.id!] = {};
    for (let i = 0; i < (doc.numPages || 0); i++) {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      blocks[doc.id!][i] = [];
    }
    doc.blocks?.forEach((block) => {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      if (block.blockType === 'ReadOnlyText') {
        block.blockType = BLOCK_TYPE_KEYS.TEXT;
        block.readOnly = true;
        // @ts-expect-error -- This code was written prior to enforcing lint rules
      } else if (block.blockType === 'ReadOnlyCheckbox') {
        block.blockType = BLOCK_TYPE_KEYS.CHECKBOX;
        block.readOnly = true;
      } else if (block.blockType === BLOCK_TYPE_KEYS.STRIKE) {
        block.height = block.height ?? STRIKE_LINE_WIDTH;
      }
      if (block.blockType === BLOCK_TYPE_KEYS.TEXT && block.value && !block.fontSize) {
        block.fontSize = calculateFontSize(block.value as string, 22, block.height!, block.width!, zoom);
      }
      if (block.blockType === BLOCK_TYPE_KEYS.CHECKBOX && typeof block.value === 'string') {
        block.value = block.value === 'true';
      }

      // @ts-expect-error -- This code was written prior to enforcing lint rules
      blocks[doc.id!][block.pageNumber].push(block);
    });
  });

  const groups = {};
  template.documents.forEach((doc) => {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    groups[doc.id!] = {};
    for (let i = 0; i < (doc.numPages || 0); i++) {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      groups[doc.id!][i] = {};
    }
    doc.blockGroups.forEach((group) => {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      groups[doc.id!][group.pageNumber][group.id] = group;
    });
  });
  const pageDimensions: IPageDimensions = {};
  template.documents.forEach((doc) => {
    pageDimensions[doc.id!] = doc.pageDimensions;
  });
  template.recipients.forEach((rec: ITemplateRecipient) => {
    if (rec.recipientTitle === 'Me') {
      rec.recipientColorCounter = 0;
    }
  });
  sortRecipients(template.recipients);
  template.recipients.forEach((rec: ITemplateRecipient, i: number) => {
    rec.color = getRecipientColor(i + 1, rec.recipientTitle);
  });

  return {
    blocks,
    groups,
    // Forcing template document shape into envelope document shape
    // See ITemplateWithMutilatedDocuments type docs
    documents: template.documents.map((doc) => ({
      fileName: doc.name,
      url: doc.links?.downloadPDF?.href,
      documentId: doc.id ?? '',
      numberOfPages: doc.numPages,
      uploadStatus: DocumentStatus.Uploaded,
      isDownloaded: true,
    })),
    // TODO: Why do we have to force the template shape into the envelope shape??
    signers: template.recipients
      .filter((r: ITemplateRecipient) => r.role === RecipientRole.Signer)
      .map((recipient: ITemplateRecipient, _i: number) => {
        const name = recipient.recipientTitle;
        return {
          signerId: recipient.id,
          color: recipient.color,
          recipientColorCounter: recipient.recipientColorCounter,
          firstName: name,
          lastName: '',
          email: '',
          signingStatus: 'InProgress',
        };
      }),
    // Fake properties trying to force the template shape into the envelope shape
    signingGroups: [],
    config: { areLegalLinksVisible: true },
    pageDimensions,
    carbonCopy: [],
    emailConfig: {
      subject: '',
      body: '',
      loading: false,
    },
    envelopeId: template.id,
    expiration: new Date(),
    status: 'All',
  };
}

export function* templateBlockCreate(documentId: string, _pageIndex: number, block: IBlock) {
  try {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const templateId = yield select(getId);
    const templateApiBlock = prepareBlockForTemplateService(block);
    const { result }: ITemplateResponse<any> = yield call(
      templateApi.addBlock,
      templateId,
      documentId,
      templateApiBlock
    );
    return result.id;
  } catch {
    yield put(errorToast('Failed to create block, please try again!'));
  }
}

export function* templateDeleteBlocks(documentId: string, pageIndex: number, blockIds: string[]) {
  try {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const templateId = yield select(getId);
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const pageGroups = yield select((state: IRootState) => state.senderBuild.groups[documentId][pageIndex]);

    let remainingBlockIds = [...blockIds];

    const apiCalls: CallEffect[] = [];
    // If we're deleting entire block groups, we make those calls separately
    for (const groupId in pageGroups) {
      const blockGroupIds = pageGroups[groupId].blockIds;
      if (blockGroupIds.every((blockId: string) => remainingBlockIds.includes(blockId))) {
        apiCalls.push(call(templateApi.deleteBlockGroup, templateId, documentId, groupId));
        remainingBlockIds = remainingBlockIds.filter((blockId) => !blockGroupIds.includes(blockId));
      }
    }

    // Delete remaining blocks not deleted from groups being deleted
    if (remainingBlockIds.length) {
      apiCalls.push(call(templateApi.deleteBlocks, templateId, remainingBlockIds));
    }
    yield all(apiCalls);
  } catch {
    yield put(errorToast('Failed to delete block, please try again!'));
  }
}

export function* templateEditBlock(documentId: string, pageIndex: number, blockIndex: number, updateObj: any) {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const originalBlock = yield select((state: IRootState) => {
    return state.senderBuild.blocks[documentId][pageIndex][blockIndex];
  });
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const templateId = yield select(getId);
  const cleanBlock = cleanUpInvalidBlockValuesAndLogToFaro(
    originalBlock,
    templateId,
    'templateEditBlock: Block had invalid values',
    true
  );
  try {
    yield put(actions.setAutoSaving(true));
    const newBlock = { ...cleanBlock, ...updateObj };
    const templateApiBlock = prepareBlockForTemplateService(newBlock);
    yield call(templateApi.editBlock, templateId, documentId, templateApiBlock);
  } catch {
    yield put(errorToast('Failed to edit block, please try again!'));
  } finally {
    yield put(actions.setAutoSaving(false));
  }
}

export function* templateCreateGroup(group: IGroup) {
  try {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const templateId = yield select(getId);
    const { result } = yield call(templateApi.createNewGroup, templateId, group);
    return result.id;
  } catch {
    yield put(errorToast('Failed to create group, please try again!'));
  }
}

export function* templateGroupEdit(documentId: string, pageIndex: number, groupId: string, updateObj: any) {
  try {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const originalGroup = yield select((state: IRootState) => {
      return state.senderBuild.groups[documentId][pageIndex][groupId];
    });
    const newGroup = { ...originalGroup, ...updateObj };
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const templateId = yield select(getId);
    yield call(templateApi.editBlockGroup, templateId, newGroup);
  } catch {
    yield put(errorToast('Failed to edit group, please try again!'));
  }
}

export function* createBlocksThenGroup(newBlocks: IBlock[], newGroup: IGroup) {
  const apiCalls: Generator[] = [];
  newBlocks.forEach((newBlock) => {
    delete newBlock.blockId;
    delete newBlock.groupId;
    apiCalls.push(templateBlockCreate(newGroup.documentId, newGroup.pageNumber, newBlock));
  });
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const blockIds = yield all(apiCalls);
  newBlocks.forEach((newBlock, i) => {
    newBlock.blockId = blockIds[i];
  });
  newGroup.blockIds.push(...blockIds);
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const groupId = yield templateCreateGroup(newGroup);
  newGroup.id = groupId;
  for (let i = 0; i < newBlocks.length; i++) {
    newBlocks[i].groupId = groupId;
  }
}
